import React, { useEffect, useState } from "react";
import { message, Table, Tag, Space, Popconfirm, Input, Alert } from "antd";
import { CancelButton, ModalComponent, trans, ButtonComponent } from "@comps/components";
import { makeRequest } from "@utils/helpers";
import { getDisbursementsReq, voidDisbursementReq } from "../requests";
import { Const } from "../../constants";
import dayjs from "dayjs";

const langKey = Const["myPayslip"].lngKey;

const statusColor = { paid: "green", void: "red" };
const statusLabel = { paid: "Released", void: "Void" };
const releaseTagColor = { "Released": "green", "Partially Released": "orange", "Ready To Release": "default", "On Hold": "red" };

const DisbursementHistory = (props) => {
  const record = props.record; // payslip
  const [loader, setLoader] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [voidingId, setVoidingId] = useState(null);
  const [voidReason, setVoidReason] = useState("");

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, []);

  const loadData = () => {
    makeRequest(setLoader, getDisbursementsReq, { payslip_id: record.id }, onSuccess, onError);
  };
  const onSuccess = (data, res) => {
    const payload = res.data || {};
    setRows(payload.disbursements || []);
    setSummary({
      net_salary: payload.net_salary,
      paid_amount: payload.paid_amount,
      remaining: payload.remaining,
      release_status: payload.release_status,
    });
  };
  const onError = (err, res) => {
    message.error(res?.response?.data?.message || "Failed to load disbursements");
  };

  const onVoid = (id) => {
    if (!voidReason.trim()) {
      message.error("Please enter a reason to void");
      return;
    }
    makeRequest(setLoader, voidDisbursementReq, { id, void_reason: voidReason }, () => {
      message.success("Release voided");
      setVoidingId(null);
      setVoidReason("");
      loadData();
    }, onError);
  };

  const columns = [
    { key: "paid_at", title: trans(`${langKey}.paidAt`), render: (_, r) => (r.paid_at ? dayjs(r.paid_at).format("YYYY-MM-DD") : "-") },
    { key: "amount", title: trans(`${langKey}.paymentAmount`), render: (_, r) => "Rs " + r.amount },
    { key: "method", title: trans(`${langKey}.method`), render: (_, r) => (r.method || "-") },
    { key: "reference_no", title: trans(`${langKey}.referenceNo`), render: (_, r) => (r.reference_no || "-") },
    {
      key: "status", title: trans(`${langKey}.status`),
      render: (_, r) => <Tag color={ statusColor[r.status] }>{ statusLabel[r.status] || r.status }</Tag>,
    },
    { key: "disbursed_by", title: trans(`${langKey}.disbursedBy`), render: (_, r) => (r.disbursed_by ? r.disbursed_by.full_name_code : "-") },
    {
      key: "proof", title: trans(`${langKey}.proof`),
      render: (_, r) => (r.proof_url ? <a href={ r.proof_url } target="_blank" rel="noreferrer">View</a> : "-"),
    },
    {
      key: "actions", title: "",
      render: (_, r) => (
        r.status === "paid" ? (
          voidingId === r.id ? (
            <Space>
              <Input size="small" placeholder="Reason" value={ voidReason } onChange={ (e) => setVoidReason(e.target.value) } style={ { width: 140 } } />
              <Popconfirm title="Void this payment?" onConfirm={ () => onVoid(r.id) }>
                <ButtonComponent size="small" danger text="Confirm" />
              </Popconfirm>
              <ButtonComponent size="small" text="Cancel" onClick={ () => { setVoidingId(null); setVoidReason(""); } } />
            </Space>
          ) : (
            <ButtonComponent size="small" danger text={ trans(`${langKey}.void`) } onClick={ () => setVoidingId(r.id) } />
          )
        ) : null
      ),
    },
  ];

  const footer = [<CancelButton key="close_button" onClick={ () => props.onCompleted(false) } />];

  return (
    <ModalComponent top={ 20 } width={ 1000 } maskClosable={ false }
      mainTitle={ trans(`${langKey}.disbursementHistory`) } subTitle={ record.requestor?.full_name_code || "" }
      visible={ true } footer={ footer } onCancel={ () => props.onCompleted(false) }>
      <Alert type="info" showIcon style={ { marginBottom: 16 } }
        message={
          <span>
            Net Salary: { summary.net_salary ?? "-" } &nbsp;|&nbsp;
            Released: { summary.paid_amount ?? "-" } &nbsp;|&nbsp;
            Pending: { summary.remaining ?? "-" } &nbsp;|&nbsp;
            Status: <Tag color={ releaseTagColor[summary.release_status] || "default" }>{ summary.release_status ?? "-" }</Tag>
          </span>
        } />
      <Table
        rowKey="id"
        loading={ loader }
        columns={ columns }
        dataSource={ rows }
        pagination={ false }
        size="small"
        scroll={ { x: "max-content" } }
      />
    </ModalComponent>
  );
};

export default DisbursementHistory;
